import React from 'react';

const C = {
  navyA: '#0a0e2b', navyB: '#111b5e', navy: '#0f1847', navyCard: '#1a2666',
  blueDeep: '#111b5e', royalBlue: '#1a3a8f',
  gold: '#D4AF37', goldDark: '#B8960C',
  blueGray: '#8a9cc5', lightBlue: '#c5cfe8', blueTint: '#b0b8cc',
  empGray: '#5a6a8a', purple: '#8B6FC0', teal: '#22a06b',
  greenActive: '#16a34a', greenBg: '#f0fdf4',
  redAlert: '#dc2626', redBg: '#fef2f2',
  pageBg: '#f4f5f8', card: '#ffffff', border: '#edf0f5', rowDiv: '#f0f1f4',
};

const ROLE_COLORS = {
  OWNER: C.gold, MGR: C.royalBlue, SUPER: C.purple,
  READING: C.teal, EMP: C.empGray,
};

const ROLE_LABELS = {
  OWNER: 'OWNER', MGR: 'MGR', SUPER: 'SUPER',
  READING: 'READING', EMP: 'EMP',
};

function getInitials(name) {
  return (name || '').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

function inferRole(user) {
  if (user.investorPercent) return 'OWNER';
  if (user.basePay || user.bonusPercent || user.managerThreshold) return 'MGR';
  return 'EMP';
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch (e) { return d; }
}

class LocationDetailView extends React.Component {
  constructor(props) {
    super(props);
    this.state = { tab: 'subscription' };
  }

  render() {
    const { locationDetail, location } = this.props;
    const { tab } = this.state;

    if (!locationDetail) {
      return (
        <div style={{ minHeight: '100vh', background: C.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: C.blueGray }}>Loading...</div>
        </div>
      );
    }

    const { company, currentSubscription, paymentHistory, profile } = locationDetail;
    const roles = this.props.user?.authority || [];
    const isActive = currentSubscription?.isActive;
    const planName = company?.planName || location?.planName || 'Not set';
    const price = currentSubscription?.amount || location?.amount || 0;

    // Merge people data
    const owners = profile?.localAdmins || [];
    const managers = profile?.managers || [];
    const employees = profile?.employees || [];
    const emergency = profile?.emergencyContacts?.filter((e) => e.name) || [];

    const ownerCount = owners.length;
    const mgrCount = managers.length;
    const empCount = employees.length;

    const font = "'SF Pro Display','Segoe UI',-apple-system,sans-serif";

    return (
      <div style={{ minHeight: '100vh', background: C.pageBg, fontFamily: font }}>
        {/* Company Hero */}
        <div style={{
          background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
          padding: '16px 14px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14, flexShrink: 0,
              background: 'rgba(212,175,55,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.gold, fontSize: 18, fontWeight: 800,
            }}>
              {getInitials(company?.name || location?.name)}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>
                {company?.name || location?.name}
              </div>
              <div style={{ fontSize: 13, color: C.blueGray }}>
                {planName} {location?.companyCode ? `· #${location.companyCode}` : ''}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 4,
              color: '#fff',
              background: isActive ? C.greenActive : C.redAlert,
            }}>
              {isActive ? 'ACTIVE' : 'UNPAID'}
            </span>
            <span style={{ fontSize: 12, color: C.blueGray }}>
              Next billing: {fmtDate(currentSubscription?.expiryDate)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', background: C.card,
          borderBottom: `2px solid ${C.border}`,
        }}>
          {['subscription', 'team'].map((t) => (
            <div
              key={t}
              onClick={() => this.setState({ tab: t })}
              style={{
                flex: 1, padding: '10px 0', textAlign: 'center', cursor: 'pointer',
                fontSize: 14, fontWeight: 700,
                color: tab === t ? C.gold : C.blueGray,
                borderBottom: `3px solid ${tab === t ? C.gold : 'transparent'}`,
                transition: 'all .15s',
              }}
            >
              {t === 'subscription' ? 'Subscription' : 'Team'}
            </div>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: '12px 14px' }}>

          {/* ── SUBSCRIPTION TAB ── */}
          {tab === 'subscription' && (
            <>
              {/* Current Plan */}
              <div style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.navy }}>{planName}</div>
                  <div>
                    <span style={{ fontSize: 22, fontWeight: 800, color: C.gold }}>${price}</span>
                    <span style={{ fontSize: 14, fontWeight: 500, color: C.blueGray }}>/mo</span>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.blueGray, textAlign: 'right' }}>
                  Manage subscription<br/>in Settings
                </div>
              </div>

              {/* Billing Grid */}
              <div style={{ ...cardStyle }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.blueGray, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Billing Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
                  {[
                    { label: 'Status', value: isActive ? 'Active' : 'Unpaid', color: isActive ? C.greenActive : C.redAlert },
                    { label: 'Duration', value: currentSubscription?.duration || '—' },
                    { label: 'Start Date', value: fmtDate(currentSubscription?.startDate) },
                    { label: 'Expiry Date', value: fmtDate(currentSubscription?.expiryDate) },
                    { label: 'Auto-Renewal', value: 'Yes', color: C.greenActive },
                  ].map((item) => (
                    <div key={item.label}>
                      <div style={{ fontSize: 11, color: C.blueGray }}>{item.label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: item.color || C.navy }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emergency Contacts */}
              {roles.includes('ROLE_OWNER') && emergency.length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.blueGray, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Emergency Contacts
                  </div>
                  {emergency.map((c, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: i < emergency.length - 1 ? `1px solid ${C.rowDiv}` : 'none',
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{c.name || '—'}</span>
                      {c.phone && <span style={{ fontSize: 13, fontWeight: 600, color: C.royalBlue }}>{c.phone}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Payment History */}
              <div style={cardStyle}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.blueGray, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Payment History
                </div>
                {/* Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 60px 60px 50px',
                  padding: '6px 0', borderBottom: `1.5px solid ${C.border}`,
                  fontSize: 11, fontWeight: 800, color: C.blueGray, textTransform: 'uppercase',
                }}>
                  <div>Date</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                  <div style={{ textAlign: 'right' }}>Duration</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                </div>
                {/* Rows */}
                {(paymentHistory || []).length > 0 ? paymentHistory.map((p, i) => {
                  const isTrial = p.amount === '0' || p.amount === 0;
                  const isPaid = !isTrial && Number(p.amount) > 0;
                  return (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '1fr 60px 60px 50px',
                      padding: '8px 0', alignItems: 'center',
                      borderBottom: i < paymentHistory.length - 1 ? `1px solid ${C.rowDiv}` : 'none',
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{fmtDate(p.startDate)}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, textAlign: 'right',
                        color: isTrial ? C.greenActive : isPaid ? C.navy : C.redAlert,
                      }}>
                        {isTrial ? 'FREE' : `$${p.amount}`}
                      </div>
                      <div style={{ fontSize: 12, textAlign: 'right', color: C.blueGray }}>{p.duration || '—'}</div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
                          background: isTrial || isPaid ? C.greenBg : C.redBg,
                          color: isTrial || isPaid ? C.greenActive : C.redAlert,
                        }}>
                          {isTrial ? 'TRIAL' : isPaid ? 'PAID' : 'UNPAID'}
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <div style={{ padding: 12, textAlign: 'center', color: C.blueGray, fontSize: 12 }}>
                    No payments found
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── TEAM TAB ── */}
          {tab === 'team' && (
            <>
              {/* KPI Summary */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[
                  { n: ownerCount, label: 'OWNERS', color: C.gold },
                  { n: mgrCount, label: 'MANAGERS', color: C.royalBlue },
                  { n: empCount, label: 'EMPLOYEES', color: C.empGray },
                ].map((k) => (
                  <div key={k.label} style={{
                    flex: 1, background: C.card, borderRadius: 10,
                    padding: '10px 12px', textAlign: 'center',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>{k.n}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: k.color, letterSpacing: 0.5 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Company Owners */}
              {owners.length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.gold, marginBottom: 8 }}>
                    COMPANY OWNERS
                  </div>
                  {owners.map((o, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                      borderBottom: i < owners.length - 1 ? `1px solid ${C.rowDiv}` : 'none',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 11, fontWeight: 800,
                      }}>
                        {getInitials(o.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{o.name}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.gold }}>Company Owner</div>
                      </div>
                      {o.phone && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.royalBlue }}>{o.phone}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Managers */}
              {managers.length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.royalBlue, marginBottom: 8 }}>
                    MANAGERS
                  </div>
                  {managers.map((m, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                      borderBottom: i < managers.length - 1 ? `1px solid ${C.rowDiv}` : 'none',
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: '#eef2ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: C.royalBlue, fontSize: 11, fontWeight: 800,
                      }}>
                        {getInitials(m.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{m.name}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.royalBlue }}>Manager</div>
                      </div>
                      {m.phone && (
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.royalBlue }}>{m.phone}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Employees */}
              {employees.length > 0 && (
                <div style={cardStyle}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.empGray, marginBottom: 8 }}>
                    EMPLOYEES
                  </div>
                  {/* Header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '32px 1fr 50px 60px',
                    padding: '4px 0', borderBottom: `1.5px solid ${C.border}`,
                    fontSize: 11, fontWeight: 800, color: C.blueGray, textTransform: 'uppercase',
                  }}>
                    <div />
                    <div>Name</div>
                    <div style={{ textAlign: 'right' }}>Rate</div>
                    <div style={{ textAlign: 'right' }}>Role</div>
                  </div>
                  {/* Rows */}
                  {employees.map((emp, i) => {
                    const role = inferRole(emp);
                    const roleColor = ROLE_COLORS[role] || C.empGray;
                    const hasRate = emp.hourlyRate && Number(emp.hourlyRate) > 0;
                    return (
                      <div key={i} style={{
                        display: 'grid', gridTemplateColumns: '32px 1fr 50px 60px',
                        padding: '6px 0', alignItems: 'center',
                        borderBottom: i < employees.length - 1 ? `1px solid ${C.rowDiv}` : 'none',
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: 7,
                          background: `${roleColor}15`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: roleColor, fontSize: 10, fontWeight: 800,
                        }}>
                          {(emp.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{emp.name}</div>
                          {emp.rateLastUpdated && (
                            <div style={{ fontSize: 11, color: C.blueTint }}>
                              {fmtDate(emp.rateLastUpdated)}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, textAlign: 'right', color: hasRate ? C.navy : C.blueTint }}>
                          {hasRate ? `$${emp.hourlyRate}/hr` : '--'}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
                            background: `${roleColor}12`, color: roleColor,
                          }}>
                            {ROLE_LABELS[role]}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: C.blueGray }}>
                    Manage employees in Settings
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

const cardStyle = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '14px 16px',
  marginBottom: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export default LocationDetailView;
